import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { APP_BUILDER_BUILD_TAB_PATH, CRM_CLIENTS_PAGE } from 'src/constants';
import { useFeatureFlags } from 'src/context';
import { OnboardingTaskStatus } from 'src/entities/OnboardingStatus';
import useNavigate from 'src/hooks/useNavigate';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { TaskCodes } from 'src/legacy/components/HomePage/OnboardingTasksSection/constants';
import {
  AppBuilder,
  useListAppBuildersQuery,
} from 'src/services/api/appBuilderApi';
import { useGetOnboardingTaskStatusQuery } from 'src/services/api/improvedOnboardingApi';
import { togglePrimarySidebarMobile } from 'src/store/ui/actions';

import {
  GUIDED_TOUR_FIRST_CLIENT,
  GUIDED_TOUR_PARAM,
  STUDIO_CHECKLIST_TASK_IDS,
  StudioChecklistTask,
  StudioChecklistTaskId,
  StudioChecklistTaskStatus,
} from './studioChecklist';

// Fallback destinations. `publishApp` falls back to the new-build page only when
// the user has no draft to resume (see getMostRecentDraftAppBuilder below).
const TASK_ROUTES: Record<StudioChecklistTaskId, string> = {
  publishApp: APP_BUILDER_BUILD_TAB_PATH,
  // Carry the guided-tour intent so the CRM page starts the coachmark tour on arrival.
  explorePortal: `${CRM_CLIENTS_PAGE.path}?${GUIDED_TOUR_PARAM}=${GUIDED_TOUR_FIRST_CLIENT}`,
};

const APP_BUILDER_SESSION_PATH_PREFIX = '/app-setup/build/session/';

// A "draft" app has been created but never published (published apps carry a
// deploymentUrl) and isn't in a terminal failed/stopped state — i.e. something
// the user can still take to publishing.
const isDraftAppBuilder = (app: AppBuilder): boolean =>
  !app.deploymentUrl && app.status !== 'failed' && app.status !== 'stopped';

// The most recently created draft, so "publish your first app" resumes the app
// the user just built instead of dropping them on a blank new build. createdAt
// is an ISO-8601 string, so lexical comparison orders it chronologically.
const getMostRecentDraftAppBuilder = (
  apps: AppBuilder[] | undefined,
): AppBuilder | undefined => {
  const drafts = (apps ?? []).filter(isDraftAppBuilder);
  if (drafts.length === 0) return undefined;
  // Seedless reduce: the first draft seeds the accumulator, so `latest` is
  // always defined and the comparison keeps the newest createdAt.
  return drafts.reduce((latest, app) =>
    app.createdAt > latest.createdAt ? app : latest,
  );
};

// Maps each sidebar checklist task to the backend onboarding task code it reflects.
const TASK_CODE_BY_ID: Record<StudioChecklistTaskId, TaskCodes> = {
  publishApp: TaskCodes.TaskCodeFirstApp,
  explorePortal: TaskCodes.TaskCodeFirstClient,
};

// The backend status strings and the checklist enum share the same values; map
// explicitly so the enum-typed UI stays type-safe.
const toChecklistStatus = (
  status: OnboardingTaskStatus | undefined,
): StudioChecklistTaskStatus => {
  switch (status) {
    case 'in-progress':
      return StudioChecklistTaskStatus.InProgress;
    case 'done':
      return StudioChecklistTaskStatus.Done;
    default:
      return StudioChecklistTaskStatus.Todo;
  }
};

export interface UseStudioChecklistResult {
  tasks: StudioChecklistTask[];
  /** Whole-number completion percentage (0-100) for the progress bar. */
  completionPct: number;
  isCollapsed: boolean;
  toggleCollapse: () => void;
  /** Navigates to the task's destination route. */
  onTaskClick: (taskId: StudioChecklistTaskId) => void;
  /** Navigates to a route and closes the primary sidebar on mobile. */
  navigateAndCloseMobileSidebar: (path: string) => void;
  shouldRender: boolean;
  /**
   * True once every studio task is done. Drives the "You're all set" title while
   * the card stays mounted for the rest of the session (it hides on next load).
   */
  allTasksCompleted: boolean;
}

export const useStudioChecklist = (): UseStudioChecklistResult => {
  const { navigate } = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { EnableAppBuilder } = useFeatureFlags();
  const { data: onboardingStatus } = useGetOnboardingTaskStatusQuery({});
  const { data: builderApps } = useListAppBuildersQuery(undefined, {
    skip: !EnableAppBuilder,
  });
  const isPrimarySidebarOpenOnMobile = useAppSelector(
    (state) => state.ui.primarySideBarMobile.isOpen,
  );

  // Collapse is ephemeral UI state — not worth persisting.
  const [isCollapsed, setIsCollapsed] = useState(false);
  const toggleCollapse = useCallback(() => setIsCollapsed((prev) => !prev), []);

  const navigateAndCloseMobileSidebar = useCallback(
    (path: string) => {
      navigate(path);
      // Match sidebar nav items: close the primary sidebar on mobile after navigating.
      if (isPrimarySidebarOpenOnMobile) {
        dispatch(togglePrimarySidebarMobile({ isOpen: false }));
      }
    },
    [navigate, dispatch, isPrimarySidebarOpenOnMobile],
  );

  const onTaskClick = useCallback(
    (taskId: StudioChecklistTaskId) => {
      let route = TASK_ROUTES[taskId];
      if (taskId === 'publishApp') {
        const draft = getMostRecentDraftAppBuilder(builderApps);
        if (draft) {
          route = `${APP_BUILDER_SESSION_PATH_PREFIX}${draft.id}`;
        }
      }
      navigateAndCloseMobileSidebar(route);
    },
    [navigateAndCloseMobileSidebar, builderApps],
  );

  const tasks: StudioChecklistTask[] = useMemo(() => {
    const taskLabels: Record<StudioChecklistTaskId, string> = {
      publishApp: t(
        ($) => $.page_apps.app_builder.getting_started.tasks.publish_app,
      ),
      explorePortal: t(
        ($) => $.page_apps.app_builder.getting_started.tasks.explore_portal,
      ),
    };
    const statusByCode = new Map(
      (onboardingStatus?.additionalFields.tasks ?? []).map((task) => [
        task.taskCode,
        task.status,
      ]),
    );
    return STUDIO_CHECKLIST_TASK_IDS.map((id) => ({
      id,
      label: taskLabels[id],
      status: toChecklistStatus(statusByCode.get(TASK_CODE_BY_ID[id])),
    }));
  }, [t, onboardingStatus]);

  const completedCount = tasks.filter(
    (task) => task.status === StudioChecklistTaskStatus.Done,
  ).length;
  const inProgressCount = tasks.filter(
    (task) => task.status === StudioChecklistTaskStatus.InProgress,
  ).length;
  // In-progress tasks count as half so the bar advances when a step is started.
  const completionPct = Math.round(
    ((completedCount + inProgressCount * 0.5) / tasks.length) * 100,
  );

  const studioTasks = onboardingStatus?.additionalFields.tasks ?? [];
  const hasLoaded = studioTasks.length > 0;
  const allTasksCompleted = hasLoaded && studioTasks.every((task) => task.done);

  const completedOnLoadRef = useRef<boolean | null>(null);
  if (completedOnLoadRef.current === null && hasLoaded) {
    completedOnLoadRef.current = allTasksCompleted;
  }
  const hiddenOnLoad = completedOnLoadRef.current === true;

  return {
    tasks,
    completionPct,
    isCollapsed,
    toggleCollapse,
    onTaskClick,
    navigateAndCloseMobileSidebar,
    shouldRender: !hiddenOnLoad,
    allTasksCompleted,
  };
};
